import { t } from '@lingui/core/macro';
import { AutoRenewalStatus, SubscriptionState } from '../enums';
import { Subscription } from '../types/Subscription.types';

/**
 * Checks if a subscription has a pending tier switch (state == SWITCHING)
 */
export const isSwitching = (subscription: Subscription): boolean => {
  return subscription.state === SubscriptionState.SWITCHING;
};

/**
 * Whether the switch affordance should be offered from this subscription.
 * Only an ongoing subscription supports switching: active-and-renewing, or already
 * switching (where the user can change the upcoming target). A cancelled/ending/expired
 * plan is terminal for switching — the user buys a fresh plan instead (avoids SU114.1).
 */
export const isSwitchable = (subscription: Subscription): boolean => {
  if (subscription.canceled) {
    return false;
  }
  const isActiveRenewing =
    subscription.state === SubscriptionState.RENEWING &&
    subscription.autoRenewal === AutoRenewalStatus.ACTIVE;
  return isActiveRenewing || isSwitching(subscription);
};

/**
 * Whether a subscription has reached a terminal state and is no longer the user's
 * current holding: it was switched away (SWITCHED), cancelled (CANCELLED), or expired
 * (EXPIRED). Such a plan is treated like an available plan again — no "Manage"/"Reactivate";
 * the user can purchase or switch to it. (A front-cancelled, still-valid plan stays RENEWING
 * with auto-renewal off and is NOT terminal, so it keeps Manage → Reactivate.)
 */
export const isSubscriptionTerminal = (subscription: Subscription): boolean => {
  return (
    subscription.state === SubscriptionState.SWITCHED ||
    subscription.state === SubscriptionState.CANCELLED ||
    subscription.state === SubscriptionState.EXPIRED
  );
};

/**
 * Whether a subscription has expired or its automatic renewal has failed (payment failure).
 */
export const isSubscriptionExpired = (subscription: Subscription): boolean => {
  if (subscription.autoRenewal === AutoRenewalStatus.FAILED) {
    return true;
  }
  if (!subscription.validUntil) {
    return false;
  }
  return new Date(subscription.validUntil) <= new Date();
};

/**
 * Checks if a subscription is expiring (INACTIVE with validUntil in the future)
 */
export const isSubscriptionExpiring = (subscription: Subscription): boolean => {
  if (subscription.autoRenewal !== AutoRenewalStatus.INACTIVE || !subscription.validUntil) {
    return false;
  }
  const validUntilDate = new Date(subscription.validUntil);
  const now = new Date();
  return validUntilDate > now;
};

/**
 * Checks if a subscription is active or expiring.
 * A subscription with a pending switch (SWITCHING) is still the user's current active plan.
 */
export const isSubscriptionActiveOrExpiring = (subscription: Subscription): boolean => {
  if (subscription.autoRenewal === AutoRenewalStatus.ACTIVE || isSwitching(subscription)) {
    return true;
  }
  return isSubscriptionExpiring(subscription);
};

/**
 * Determines the status badge for a subscription.
 * Owned plans: a pending switch wins, then active-renewing, then expired/failed.
 * A cancelled-but-still-valid plan shows no badge (its tile keeps "Manage" → Reactivate).
 */
export const getSubscriptionBadge = (subscription: Subscription): string | null => {
  // Owned and still the user's current holding (not switched/cancelled/expired).
  if (subscription.boughtAt && !isSubscriptionTerminal(subscription)) {
    // A pending switch takes visual priority — it can persist until validUntil.
    if (isSwitching(subscription)) {
      return 'switching';
    }
    if (subscription.autoRenewal === AutoRenewalStatus.ACTIVE) {
      return 'active';
    }
    if (isSubscriptionExpired(subscription)) {
      return 'expired';
    }
    return null;
  }
  // Terminal (switched/cancelled/expired) plans behave like an available plan again.
  if (subscription.highlight) {
    return 'popular';
  }
  return null;
};

/**
 * Determines the CTA button text based on subscription status
 */
export const getSubscriptionCTA = (subscription: Subscription): string => {
  // Owned and still current → "Manage". A terminal plan (switched away/cancelled/expired)
  // falls back to "select" so it can be purchased or switched to again.
  if (subscription.boughtAt && !isSubscriptionTerminal(subscription)) {
    return 'manage';
  }
  return 'select';
};

/**
 * Merges available subscriptions with user's owned subscriptions
 */
export const mergeSubscriptions = (
  availableSubscriptions: Subscription[],
  mySubscriptions: Subscription[],
): Subscription[] => {
  const availableSubscriptionsMap = new Map(availableSubscriptions.map((sub) => [sub.code, sub]));
  const mySubscriptionsMap = new Map(mySubscriptions.map((sub) => [sub.code, sub]));

  const enrichedAvailableSubscriptions = availableSubscriptions.map((subscription) => {
    const userSubscription = mySubscriptionsMap.get(subscription.code);
    if (userSubscription) {
      return {
        ...subscription,
        ...userSubscription,
        details: userSubscription.details || subscription.details,
      };
    }
    return subscription;
  });

  const userOnlySubscriptions = mySubscriptions.filter(
    (userSub) => !availableSubscriptionsMap.has(userSub.code),
  );

  return [...enrichedAvailableSubscriptions, ...userOnlySubscriptions];
};

/**
 * Checks if subscription can be purchased (no active subscription exists)
 */
export const canPurchaseSubscription = (subscriptions: Subscription[]): boolean => {
  return !subscriptions.some((sub) => sub.boughtAt && sub.autoRenewal === AutoRenewalStatus.ACTIVE);
};

/**
 * Returns the billing cycle display label
 */
export const getBillingCycleLabel = (interval: string | undefined): string => {
  if (!interval) return '';

  switch (interval.toUpperCase()) {
    case 'DAILY':
      return t({ id: 'subscriptions.billingCycle.day', message: '/day' });
    case 'WEEKLY':
      return t({ id: 'subscriptions.billingCycle.week', message: '/week' });
    case 'MONTHLY':
      return t({ id: 'subscriptions.billingCycle.month', message: '/month' });
    case 'YEARLY':
      return t({ id: 'subscriptions.billingCycle.year', message: '/year' });
    case 'FIRST_OF_MONTH':
      return t({
        id: 'subscriptions.billingCycle.firstOfMonth',
        message: '/first of month',
      });
    case 'FIRST_OF_YEAR':
      return t({
        id: 'subscriptions.billingCycle.firstOfYear',
        message: '/first of year',
      });
    default:
      return `/${interval.toLowerCase()}`;
  }
};

/**
 * Returns the renewal period label for disclaimer text (e.g. "day", "month", "year")
 */
export const getRenewalPeriodLabel = (interval: string | undefined): string => {
  if (!interval) return '';

  switch (interval.toUpperCase()) {
    case 'DAILY':
      return t({ id: 'subscriptions.renewalPeriod.day', message: 'day' });
    case 'WEEKLY':
      return t({ id: 'subscriptions.renewalPeriod.week', message: 'week' });
    case 'MONTHLY':
      return t({ id: 'subscriptions.renewalPeriod.month', message: 'month' });
    case 'YEARLY':
      return t({ id: 'subscriptions.renewalPeriod.year', message: 'year' });
    case 'FIRST_OF_MONTH':
      return t({
        id: 'subscriptions.renewalPeriod.firstOfMonth',
        message: 'first of month',
      });
    case 'FIRST_OF_YEAR':
      return t({
        id: 'subscriptions.renewalPeriod.firstOfYear',
        message: 'first of year',
      });
    default:
      return interval.toLowerCase();
  }
};
